/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getGroups } from '@/features/users/api'
import { FormDirtyIndicator } from '../components/form-dirty-indicator'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

// Sentinel value for "no default — use the system fallback". Stored as the
// absence of an entry in the JSON map, but we need a distinct dropdown value
// since "" already means "no role" for user_type. Empty string is fine for
// OAuth provider names (they never have empty as a real value).
const NO_DEFAULT = '__no_default__'

// OAuth providers we want to surface a per-provider default-group control for.
// DingTalk is the production case today; the list is a UI hint — the
// underlying JSON map is open and accepts any provider key, so adding a new
// provider here doesn't require backend changes.
const KNOWN_OAUTH_PROVIDERS = ['DingTalk'] as const

// User_type values mirror the backend enum (validBatchUserTypes in
// controller/admin_batch_users.go): "" | "student" | "teacher".
const USER_TYPE_KEYS = ['student', 'teacher', ''] as const
type UserTypeKey = (typeof USER_TYPE_KEYS)[number]

function userTypeLabel(t: (key: string) => string, k: UserTypeKey): string {
  if (k === 'student') return t('Student')
  if (k === 'teacher') return t('Teacher / Staff')
  return t('Third-party (no role)')
}

type DefaultGroupsSectionProps = {
  defaultValues: {
    OAuthDefaultGroups: string
    UserTypeDefaultGroups: string
  }
}

function parseMap(json: string): Record<string, string> {
  try {
    const parsed = JSON.parse(json || '{}')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>
    }
  } catch {
    // ignore malformed JSON; treat as empty.
  }
  return {}
}

export function DefaultGroupsSection({
  defaultValues,
}: DefaultGroupsSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: getGroups,
    staleTime: 5 * 60 * 1000,
  })
  const groups = groupsData?.data || []

  // Parse the JSON strings into in-memory maps. Empty/missing entries are
  // represented as NO_DEFAULT in the select state.
  const initialOAuth = useMemo(
    () => parseMap(defaultValues.OAuthDefaultGroups),
    [defaultValues.OAuthDefaultGroups]
  )
  const initialUserType = useMemo(
    () => parseMap(defaultValues.UserTypeDefaultGroups),
    [defaultValues.UserTypeDefaultGroups]
  )

  const [oauthMap, setOauthMap] = useState<Record<string, string>>(initialOAuth)
  const [userTypeMap, setUserTypeMap] =
    useState<Record<string, string>>(initialUserType)

  // Reset to saved values whenever the backend-loaded defaults change (e.g.
  // settings page re-fetches). React 19 recommended "store previous value
  // during render" pattern replaces the old useEffect-based reset.
  const [prevInitialOAuth, setPrevInitialOAuth] = useState(initialOAuth)
  if (prevInitialOAuth !== initialOAuth) {
    setPrevInitialOAuth(initialOAuth)
    setOauthMap(initialOAuth)
  }
  const [prevInitialUserType, setPrevInitialUserType] = useState(initialUserType)
  if (prevInitialUserType !== initialUserType) {
    setPrevInitialUserType(initialUserType)
    setUserTypeMap(initialUserType)
  }

  const isDirty =
    JSON.stringify(oauthMap) !== JSON.stringify(initialOAuth) ||
    JSON.stringify(userTypeMap) !== JSON.stringify(initialUserType)

  const setOAuthEntry = (provider: string, value: string) => {
    setOauthMap((prev) => {
      const next = { ...prev }
      if (value === NO_DEFAULT) delete next[provider]
      else next[provider] = value
      return next
    })
  }

  const setUserTypeEntry = (userType: string, value: string) => {
    setUserTypeMap((prev) => {
      const next = { ...prev }
      if (value === NO_DEFAULT) delete next[userType]
      else next[userType] = value
      return next
    })
  }

  const handleSubmit = async () => {
    try {
      // Both options always saved — small enough that diffing per-key isn't
      // worth the complexity.
      await updateOption.mutateAsync({
        key: 'OAuthDefaultGroups',
        value: JSON.stringify(oauthMap),
      })
      await updateOption.mutateAsync({
        key: 'UserTypeDefaultGroups',
        value: JSON.stringify(userTypeMap),
      })
      toast.success(t('Default group settings saved'))
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('Failed to save'))
    }
  }

  const handleReset = () => {
    setOauthMap(initialOAuth)
    setUserTypeMap(initialUserType)
    toast.success(t('Form reset to saved values'))
  }

  const renderGroupSelect = (
    id: string,
    value: string | undefined,
    onChange: (next: string) => void
  ) => {
    const selected = value ?? NO_DEFAULT
    const items = [
      { value: NO_DEFAULT, label: t('Use system default') },
      ...groups.map((g) => ({ value: g, label: g })),
    ]
    return (
      <Select
        items={items}
        value={selected}
        onValueChange={(v) => v !== null && onChange(v)}
      >
        <SelectTrigger id={id} disabled={groups.length === 0}>
          <SelectValue placeholder={t('Select a group')} />
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false}>
          <SelectGroup>
            {items.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }

  return (
    <SettingsSection
      title={t('New User Default Groups')}
      description={t(
        'Pick the group assigned when a new account is created without an explicit override. "Use system default" falls back to the first configured group.'
      )}
    >
      <FormDirtyIndicator isDirty={isDirty} />

      <div className='space-y-6'>
        <div className='space-y-3'>
          <h4 className='text-sm font-semibold'>{t('OAuth providers')}</h4>
          <p className='text-muted-foreground text-xs'>
            {t(
              'Each OAuth provider can map its first-time users to a specific group.'
            )}
          </p>
          {KNOWN_OAUTH_PROVIDERS.map((provider) => (
            <div
              key={provider}
              className='grid grid-cols-[1fr_2fr] items-center gap-3'
            >
              <Label htmlFor={`default-group-oauth-${provider}`}>
                {provider}
              </Label>
              {renderGroupSelect(
                `default-group-oauth-${provider}`,
                oauthMap[provider],
                (v) => setOAuthEntry(provider, v)
              )}
            </div>
          ))}
        </div>

        <div className='space-y-3'>
          <h4 className='text-sm font-semibold'>
            {t('Batch creation by user type')}
          </h4>
          <p className='text-muted-foreground text-xs'>
            {t(
              'When an admin batch-creates users without specifying a group, the user_type determines the default.'
            )}
          </p>
          {USER_TYPE_KEYS.map((ut) => (
            <div
              key={ut || 'none'}
              className='grid grid-cols-[1fr_2fr] items-center gap-3'
            >
              <Label htmlFor={`default-group-usertype-${ut || 'none'}`}>
                {userTypeLabel(t, ut)}
              </Label>
              {renderGroupSelect(
                `default-group-usertype-${ut || 'none'}`,
                userTypeMap[ut],
                (v) => setUserTypeEntry(ut, v)
              )}
            </div>
          ))}
        </div>

        <div className='flex items-center gap-2'>
          <Button
            type='button'
            onClick={handleSubmit}
            disabled={!isDirty || updateOption.isPending}
          >
            {updateOption.isPending ? t('Saving...') : t('Save')}
          </Button>
          <Button
            type='button'
            variant='outline'
            onClick={handleReset}
            disabled={!isDirty || updateOption.isPending}
          >
            {t('Reset')}
          </Button>
        </div>
      </div>
    </SettingsSection>
  )
}
